/**
 * Shared animation frame system for efficient timer management
 * This system ensures all timers use a single requestAnimationFrame loop
 */

// Define the interface for timer objects
export interface TimerInstance {
  update: (currentTime: number) => void;
}

// Shared animation frame system
const sharedSystem = {
  frameId: null as number | null,
  activeTimers: new Set<TimerInstance>(),
  lastFrameTime: 0,
  targetFPS: 60,
  frameInterval: 1000 / 60, // ~16.67ms for 60fps

  // Check if there's any timer running
  hasRunningTimers(): boolean {
    return this.activeTimers.size > 0;
  },

  // Add a timer to the active set
  addTimer(timer: TimerInstance): void {
    this.activeTimers.add(timer);
    if (this.activeTimers.size === 1) {
      // First active timer, start the frame loop
      this.startFrameLoop();
    }
  },

  // Remove a timer from the active set
  removeTimer(timer: TimerInstance): void {
    this.activeTimers.delete(timer);
    if (this.activeTimers.size === 0) {
      // No more active timers, stop the frame loop
      this.stopFrameLoop();
    }
  },

  // Start the shared animation frame loop
  startFrameLoop(): void {
    if (this.frameId === null) {
      const tick = (): void => {
        const now = Date.now();
        const delta = now - this.lastFrameTime;

        // Only update if enough time has passed (frame rate control)
        if (delta >= this.frameInterval) {
          this.lastFrameTime = now;

          // Update all active timers
          this.activeTimers.forEach(timer => {
            timer.update(now);
          });
        }

        // Request next frame
        this.frameId = requestAnimationFrame(tick);
      };

      this.lastFrameTime = Date.now();
      this.frameId = requestAnimationFrame(tick);
    }
  },

  // Stop the animation frame loop
  stopFrameLoop(): void {
    if (this.frameId !== null) {
      cancelAnimationFrame(this.frameId);
      this.frameId = null;
    }
  },
};

export default sharedSystem;
